import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

// Card title row: teal title, optional subtitle, optional action on the right (e.g. <GrButton variant="text">).
// wrapAction: on phones the action (e.g. buttons + a select) moves under the title and takes the full width.
// noWrapTitle: the title stays on one line; when the row is too narrow the action drops under it.
export default function GrCardHeader({
  title,
  subtitle,
  action,
  as = "h3",
  wrapAction = false,
  noWrapTitle = false,
  sx,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: gr.space[3],
        mb: gr.space[4],
        // When space runs out the action drops under the title instead of squeezing it.
        flexWrap: "wrap",
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0, flex: noWrapTitle ? "1 0 auto" : "1 1 180px" }}>
        <Box
          component={as}
          sx={{
            m: 0,
            whiteSpace: noWrapTitle ? "nowrap" : undefined,
            fontFamily: gr.font.family,
            fontSize: gr.font.size.xl,
            fontWeight: gr.font.weight.medium,
            lineHeight: 1.35,
            color: gr.color.brandStrong, // text-safe teal (AA)
          }}
        >
          {title}
        </Box>
        {subtitle ? (
          <Box
            component="p"
            sx={{
              m: 0,
              mt: "2px",
              fontSize: gr.font.size.sm,
              color: gr.color.muted,
            }}
          >
            {subtitle}
          </Box>
        ) : null}
      </Box>
      {action ? (
        // Centred on the title line (title line-height = 18px × 1.35).
        <Box
          sx={{
            flex: "none",
            minHeight: `calc(${gr.font.size.xl} * 1.35)`,
            display: "flex",
            alignItems: "center",
            ...(wrapAction
              ? {
                  "@media (max-width: 599.95px)": {
                    flexBasis: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    "& > *": { width: "100%", minWidth: 0 },
                  },
                }
              : {}),
          }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
  );
}

GrCardHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  as: PropTypes.oneOf(["h2", "h3", "h4"]),
  wrapAction: PropTypes.bool,
  noWrapTitle: PropTypes.bool,
  sx: PropTypes.object,
};
